import React, { useState } from 'react';
import { usePayments, useRefunds } from '../../hooks/useAdmin';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  CreditCard, 
  RefreshCw, 
  Download, 
  Search,
  Filter,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Calendar,
  User,
  RotateCcw
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

const PaymentManager = () => {
  const { payments, loading: paymentsLoading, error: paymentsError, fetchPayments } = usePayments();
  const { refunds, loading: refundsLoading, error: refundsError, fetchRefunds, createRefund } = useRefunds();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refundForm, setRefundForm] = useState({
    payment_id: '',
    reason: ''
  });
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.contest_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.razorpay_payment_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const variants = {
      pending: { className: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      success: { className: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Success' },
      failed: { className: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' },
      refunded: { className: 'bg-blue-100 text-blue-800', icon: RotateCcw, label: 'Refunded' }
    };
    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;
    return (
      <Badge className={variant.className}>
        <Icon className="w-3 h-3 mr-1" />
        {variant.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const handleRefund = async (paymentId) => {
    setRefundForm({ ...refundForm, payment_id: paymentId });
    setShowRefundDialog(true);
  };

  const processRefund = async () => {
    if (!refundForm.reason.trim()) {
      return;
    }

    setProcessing(true);
    try {
      await createRefund(refundForm);
      setShowRefundDialog(false);
      setRefundForm({ payment_id: '', reason: '' });
      fetchPayments(); // Refresh payments
    } catch (error) {
      console.error('Refund processing failed:', error);
    } finally {
      setProcessing(false);
    }
  };

  const calculateStats = (data, type) => {
    if (type === 'payments') {
      const totalAmount = data.reduce((sum, payment) => sum + payment.amount, 0);
      const successfulPayments = data.filter(p => p.status === 'success');
      const successRate = data.length > 0 ? (successfulPayments.length / data.length) * 100 : 0;
      const successfulAmount = successfulPayments.reduce((sum, payment) => sum + payment.amount, 0);
      
      return {
        total: data.length,
        totalAmount,
        successRate,
        successfulAmount
      };
    } else {
      const totalRefunds = data.length;
      const processedRefunds = data.filter(r => r.status === 'processed').length;
      const totalAmount = data.reduce((sum, refund) => sum + refund.amount, 0);
      
      return {
        total: totalRefunds,
        processed: processedRefunds,
        pending: totalRefunds - processedRefunds,
        totalAmount
      };
    }
  };

  const paymentStats = calculateStats(payments, 'payments');
  const refundStats = calculateStats(refunds, 'refunds');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Management</h1>
          <p className="text-gray-600">Monitor transactions and process refunds</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              fetchPayments();
              fetchRefunds();
            }}
            disabled={paymentsLoading || refundsLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${paymentsLoading || refundsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Error Alerts */}
      {(paymentsError || refundsError) && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {paymentsError || refundsError}
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="payments" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="payments" className="flex items-center space-x-2">
            <CreditCard className="w-4 h-4" />
            <span>Payments ({payments.length})</span>
          </TabsTrigger>
          <TabsTrigger value="refunds" className="flex items-center space-x-2">
            <RotateCcw className="w-4 h-4" />
            <span>Refunds ({refunds.length})</span>
          </TabsTrigger>
        </TabsList>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-6">
          {/* Payment Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Payments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{paymentStats.total}</div>
                <p className="text-xs text-gray-500">All time</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Success Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{paymentStats.successRate.toFixed(1)}%</div>
                <p className="text-xs text-gray-500">Payment success</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(paymentStats.successfulAmount)}</div>
                <p className="text-xs text-gray-500">Successful payments</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Average Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(paymentStats.total > 0 ? paymentStats.totalAmount / paymentStats.total : 0)}
                </div>
                <p className="text-xs text-gray-500">Per transaction</p>
              </CardContent>
            </Card>
          </div>

          {/* Payment Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by user ID, contest ID, or payment ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Payment List */}
          <div className="space-y-4">
            {paymentsLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-24 bg-gray-200 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : filteredPayments.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
                  <p className="text-gray-600">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria.' 
                      : 'Payments will appear here once users start participating in contests.'
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredPayments.map((payment) => (
                <Card key={payment.id} className="hover:shadow-lg transition-shadow duration-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-full">
                          <CreditCard className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">Payment #{payment.id.slice(-8)}</h3>
                          <p className="text-sm text-gray-600">
                            {payment.razorpay_payment_id || 'No Razorpay ID'}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(payment.status)}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <div className="text-sm">
                          <p className="text-gray-600">Amount</p>
                          <p className="font-medium">{formatCurrency(payment.amount)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div className="text-sm">
                          <p className="text-gray-600">User ID</p>
                          <p className="font-medium">{payment.user_id}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div className="text-sm">
                          <p className="text-gray-600">Created</p>
                          <p className="font-medium">{formatDate(payment.created_at)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <div className="text-sm">
                          <p className="text-gray-600">Contest</p>
                          <p className="font-medium">{payment.contest_id}</p>
                        </div>
                      </div>
                    </div>
                    
                    {payment.status === 'success' && (
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRefund(payment.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Process Refund
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Refunds Tab */}
        <TabsContent value="refunds" className="space-y-6">
          {/* Refund Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Refunds</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{refundStats.total}</div>
                <p className="text-xs text-gray-500">All refund requests</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Processed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{refundStats.processed}</div>
                <p className="text-xs text-gray-500">Successfully processed</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Pending</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{refundStats.pending}</div>
                <p className="text-xs text-gray-500">Awaiting processing</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(refundStats.totalAmount)}</div>
                <p className="text-xs text-gray-500">Refunded amount</p>
              </CardContent>
            </Card>
          </div>

          {/* Refund List */}
          <div className="space-y-4">
            {refundsLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-24 bg-gray-200 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : refunds.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <RotateCcw className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No refunds found</h3>
                  <p className="text-gray-600">Refund requests will appear here when processed.</p>
                </CardContent>
              </Card>
            ) : (
              refunds.map((refund) => (
                <Card key={refund.id} className="hover:shadow-lg transition-shadow duration-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-100 rounded-full">
                          <RotateCcw className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">Refund #{refund.id.slice(-8)}</h3>
                          <p className="text-sm text-gray-600">
                            {refund.razorpay_refund_id || 'Processing...'}
                          </p>
                        </div>
                      </div>
                      <Badge className={refund.status === 'processed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {refund.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <div className="text-sm">
                          <p className="text-gray-600">Amount</p>
                          <p className="font-medium text-red-600">{formatCurrency(refund.amount)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div className="text-sm">
                          <p className="text-gray-600">User ID</p>
                          <p className="font-medium">{refund.user_id}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div className="text-sm">
                          <p className="text-gray-600">Created</p>
                          <p className="font-medium">{formatDate(refund.created_at)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Reason:</p>
                      <p className="text-sm text-gray-900">{refund.reason}</p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Payment ID</label>
              <p className="text-sm text-gray-600 mt-1">{refundForm.payment_id}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Reason for Refund *</label>
              <Textarea
                value={refundForm.reason}
                onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                placeholder="Enter reason for refund..."
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowRefundDialog(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                onClick={processRefund}
                disabled={processing || !refundForm.reason.trim()}
                className="bg-red-600 hover:bg-red-700"
              >
                {processing ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  'Process Refund'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentManager;